import { useEffect, useRef, useState } from 'react'
import type { Attachment } from '../types.js'

interface Props {
  attachments: Attachment[]
  token: string
}

type AttachmentKind = 'image' | 'video' | 'audio' | 'pdf' | 'other'

interface ViewerState {
  attachmentId: string
  url: string
  filename: string
  contentType: string
}

function kindOf(contentType: string, filename: string): AttachmentKind {
  const mime = contentType.toLowerCase()
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) return 'pdf'
  return 'other'
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  return `${(bytes / 1024).toFixed(1)} KB`
}

export function Attachments({ attachments, token }: Props) {
  const [viewer, setViewer] = useState<ViewerState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const blobCache = useRef<Record<string, string>>({})

  async function ensureUrl(id: string): Promise<string> {
    const cached = blobCache.current[id]
    if (cached) return cached
    const res = await fetch(`/widget/v1/attachments/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const text = await res.text().catch(() => 'Failed to load media')
      throw new Error(text)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    blobCache.current[id] = url
    return url
  }

  function openViewer(a: Attachment) {
    setError(null)
    ensureUrl(a.attachmentId)
      .then((url) => setViewer({ attachmentId: a.attachmentId, url, filename: a.filename, contentType: a.contentType }))
      .catch((err: Error) => setError(err.message ?? 'Failed to load media'))
  }

  function download(a: Attachment) {
    setError(null)
    ensureUrl(a.attachmentId)
      .then((url) => {
        const link = document.createElement('a')
        link.href = url
        link.download = a.filename
        link.click()
      })
      .catch((err: Error) => setError(err.message ?? 'Failed to download'))
  }

  return (
    <div className="kc3-attachments">
      {attachments.map((a) => (
        <div key={a.attachmentId} className="kc3-message__attachment">
          <span className="kc3-message__attachment-name">
            {a.filename} ({formatSize(a.size)})
          </span>
          <div className="kc3-message__attachment-actions">
            <button
              type="button"
              className="kc3-message__attachment-action"
              onClick={() => openViewer(a)}
            >
              View
            </button>
            <button
              type="button"
              className="kc3-message__attachment-action"
              onClick={() => download(a)}
            >
              Download
            </button>
          </div>
        </div>
      ))}
      {error && <div className="kc3-attachment-error">{error}</div>}
      {viewer && <MediaViewer state={viewer} onClose={() => setViewer(null)} />}
    </div>
  )
}

function MediaViewer({ state, onClose }: { state: ViewerState; onClose: () => void }) {
  const kind = kindOf(state.contentType, state.filename)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="kc3-media-viewer-overlay" role="dialog" aria-modal="true" aria-label={state.filename}>
      {/* Mouse backdrop dismiss as a real (non-tabbable) button; keyboard users have Escape and the Close button. */}
      <button type="button" className="kc3-media-viewer-backdrop" aria-label="Close preview" tabIndex={-1} onClick={onClose} />
      <div className="kc3-media-viewer-content">
        <button type="button" className="kc3-media-viewer-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        {kind === 'image' && (
          <img
            src={state.url}
            alt={state.filename}
            className="kc3-media-viewer-media"
          />
        )}
        {kind === 'video' && (
          <video src={state.url} controls autoPlay className="kc3-media-viewer-media">
            Your browser does not support video playback.
          </video>
        )}
        {kind === 'audio' && (
          <audio src={state.url} controls autoPlay className="kc3-media-viewer-audio">
            Your browser does not support audio playback.
          </audio>
        )}
        {kind === 'pdf' && (
          <iframe
            src={state.url}
            title={state.filename}
            className="kc3-media-viewer-pdf"
          />
        )}
        {kind === 'other' && (
          <div className="kc3-media-viewer-unsupported">
            <p>{state.filename}</p>
            <p className="kc3-media-viewer-hint">Preview is not available for this file type.</p>
            <button
              type="button"
              className="kc3-message__attachment-action"
              onClick={() => {
                const link = document.createElement('a')
                link.href = state.url
                link.download = state.filename
                link.click()
              }}
            >
              Download
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
